<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="bodyClassName">
      <div class="row">
        <div class="col-md-12 component-title-row">
          <h3>
            <i class="fa fa-fw fa-bullhorn"></i> {{ _('Campaigns') }}
          </h3>
          <div class="component-title-buttons">
            <button class="newSelector btn btn-success addbutton" type="button" v-on:click="createNew"><i class="fa fa-fw fa-plus"></i> {{ _('Create new...') }}</button>
          </div>
        </div>
      </div>
      <div class="campaign-order-wrap">
      <table class="table table-striped campaign-order">
        <thead>
          <tr>
            <th class="campaign-drag-col"></th>
            <th>{{ _('ID') }}</th>
            <th>{{ _('Title') }}</th>
            <th>{{ _('Type') }}</th>
            <th>{{ _('Feature flag') }}</th>
            <th class="campaign-targets-col">{{ _('Targeting') }}</th>
            <th>{{ _('Published') }}</th>
            <th>{{ _('User level') }}</th>
            <th>{{ _('Start date') }}</th>
            <th>{{ _('End date') }}</th>
            <th>{{ _('Updated') }}</th>
          </tr>
        </thead>
        <draggable v-model="campaigns" tag="tbody" draggable=".campaign-row" handle=".campaign-drag" :delay="100" :delayOnTouchOnly="true" :disabled="savingOrder" @end="orderChanged">
          <tr class="campaign-row" v-for="campaign in campaigns" :key="campaign.id" @click="editCampaign(campaign)">
            <td class="campaign-drag" @click.stop><i class="fa fa-fw fa-bars"></i></td>
            <td>{{ campaign.id }}</td>
            <td>{{ campaignTitle(campaign) }}</td>
            <td>{{ campaignType(campaign) }}</td>
            <td>{{ featureFlag(campaign) }}</td>
            <td class="campaign-targets-col">
              <span v-if="targetSummary(campaign)" :title="targetSummary(campaign)"><i class="fa fa-fw fa-crosshairs"></i> <span class="hidden-xs">{{ targetSummary(campaign) }}</span></span>
            </td>
            <td>{{ parseInt(campaign.published, 10) ? _('Yes') : _('No') }}</td>
            <td>{{ userLevel(campaign) }}</td>
            <td>{{ formatDate(campaign.start_ts) }}</td>
            <td>{{ formatDate(campaign.end_ts) }}</td>
            <td>{{ formatDateTime(campaign.updated_at) }}</td>
          </tr>
        </draggable>
      </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { DateTime } from 'luxon'
import components from '~/lib/components'
import debounce from 'lodash/debounce'

export default {
  mixins: [components],
  data () {
    return {
      campaigns: [],
      savingOrder: false
    }
  },
  methods: {
    campaignTitle (campaign) {
      return campaign.title_fi || campaign.title_en || campaign.title || ''
    },
    campaignType (campaign) {
      return campaign.type === 'categoryfeed' ? this._('Newest product in a category') : this._('Manual campaign')
    },
    featureFlag (campaign) {
      if (!campaign.feature_flag) {
        return ''
      }
      return (parseInt(campaign.feature_flag_exclude, 10) ? this._('Without') + ': ' : '') + campaign.feature_flag
    },
    // Counts only: the email list itself is not shown in the list
    targetSummary (campaign) {
      const targets = campaign.targets || {}
      const parts = []
      if (targets.shops && targets.shops.length) {
        parts.push(this._('Shops: %s').replace('%s', targets.shops.length))
      }
      if (targets.exclude_shops && targets.exclude_shops.length) {
        parts.push(this._('Excluded shops: %s').replace('%s', targets.exclude_shops.length))
      }
      if (targets.emails && targets.emails.length) {
        parts.push(this._('Emails: %s').replace('%s', targets.emails.length))
      }
      return parts.join(', ')
    },
    // JDEV-3323: 0 is "No limit"; null (older campaigns) is the admin default, 7
    userLevel (campaign) {
      const parsed = parseInt(campaign.min_userlevel, 10)
      const value = Number.isNaN(parsed) ? 7 : parsed
      if (value === 0) {
        return this._('No limit')
      }
      const names = {
        2: this._('POS user'),
        3: this._('Standard user'),
        7: this._('Administrator'),
        10: this._('System Administrator')
      }
      return names[value] ? value + ' - ' + names[value] : String(value)
    },
    formatDate (ts) {
      if (!ts) {
        return ''
      }
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone }).toFormat(this.dateFormat || 'd.M.yyyy')
    },
    formatDateTime (ts) {
      if (!ts) {
        return ''
      }
      const date = DateTime.fromSeconds(Number(ts), { zone: this.timezone })
      return date.toFormat(this.dateFormat || 'd.M.yyyy') + ' ' + this._('at') + ' ' + date.toFormat(this.timeFormat || 'H:mm')
    },
    createNew () {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'AdminCampaignDetails',
        title: this._('Campaign'),
        width: 6,
        data: {
          campaignId: 0
        }
      })
    },
    editCampaign (campaign) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'AdminCampaignDetails',
        title: this._('Campaign') + ' ' + campaign.id,
        width: 6,
        data: {
          campaignId: campaign.id
        }
      })
    },
    async loadCampaigns () {
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/campaigns.json?apikey=' + this.apiKey)
        const rows = Array.isArray(data) ? data : (data && typeof data === 'object' ? Object.values(data) : [])
        rows.sort((a, b) => (parseInt(b.priority, 10) || 0) - (parseInt(a.priority, 10) || 0) || (parseInt(b.id, 10) || 0) - (parseInt(a.id, 10) || 0))
        this.campaigns = rows
      } catch (e) {
        this.showError(e)
      }
    },
    // JDEV-3257: targets are left out on purpose. A PUT without the key keeps the campaign's targets as they are,
    // so reordering only changes the priority and can't clear or rewrite the shop and email lists.
    campaignPayload (campaign, priority) {
      return {
        id: campaign.id,
        type: campaign.type || 'manual',
        published: campaign.published ? 1 : 0,
        start_ts: campaign.start_ts || null,
        end_ts: campaign.end_ts || null,
        // Keep 0 ("No limit") as it is; only a missing value is sent as null
        min_userlevel: campaign.min_userlevel === null || campaign.min_userlevel === undefined || campaign.min_userlevel === '' ? null : parseInt(campaign.min_userlevel, 10),
        priority,
        feature_flag: campaign.feature_flag || '',
        feature_flag_exclude: campaign.feature_flag && campaign.feature_flag_exclude ? 1 : 0,
        source_shopid: campaign.type === 'categoryfeed' ? (campaign.source_shopid || '') : '',
        source_category_id: campaign.type === 'categoryfeed' ? (campaign.source_category_id || null) : null,
        title_fi: campaign.title_fi || '',
        title_en: campaign.title_en || '',
        text_fi: campaign.text_fi || '',
        text_en: campaign.text_en || '',
        cta_title_fi: campaign.cta_title_fi || '',
        cta_title_en: campaign.cta_title_en || '',
        cta_url_fi: campaign.cta_url_fi || '',
        cta_url_en: campaign.cta_url_en || ''
      }
    },
    async orderChanged () {
      if (this.savingOrder) {
        return
      }
      const count = this.campaigns.length
      const changed = []
      this.campaigns.forEach((campaign, index) => {
        const priority = count - index
        if (parseInt(campaign.priority, 10) !== priority) {
          changed.push({ campaign, priority })
        }
      })
      if (!changed.length) {
        return
      }
      this.savingOrder = true
      try {
        await Promise.all(changed.map(({ campaign, priority }) => axios.put(
          this.baseDomain + this.shopId + '/campaigns/' + campaign.id + '.json?apikey=' + this.apiKey,
          this.campaignPayload(campaign, priority)
        )))
        changed.forEach(({ campaign, priority }) => {
          campaign.priority = priority
        })
      } catch (e) {
        this.showError(e)
        this.loadCampaigns()
      } finally {
        this.savingOrder = false
      }
    }
  },
  mounted () {
    this.loadCampaigns()
    if (!this.events) {
      const events = {}
      events.campaignUpdate = debounce(() => this.loadCampaigns(), 200)
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>

<style scoped>
.campaign-order-wrap {
  margin-top: 10px;
  overflow-x: auto;
}
.campaign-order {
  margin-top: 0;
}
.campaign-drag-col {
  width: 36px;
}
.campaign-drag {
  cursor: grab;
  color: #6b7780;
  width: 36px;
}
.campaign-row {
  cursor: pointer;
}
.campaign-targets-col {
  white-space: nowrap;
}
</style>
